/* APP-148 "The step" (ForExec, LeadApprove, LeadSend boards): who it's for, files and a note,
   what happens and how it's recorded. Tokens only; every target is at least 44 px; nothing scrolls
   the page sideways. */

/* --- The dialog --------------------------------------------------------------------------------- */

.overlay {
  position: fixed;
  inset: 0;
  z-index: 70;
  display: grid;
  place-items: center;
  padding: 16px;
  background: var(--scrim);
}

.overlay[data-entering] {
  animation: fade var(--dur-fast) ease-out;
}

@keyframes fade {
  from {
    opacity: 0;
  }
}

.modal {
  width: min(860px, 100%);
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  border-radius: var(--radius-md);
  background: var(--white);
  box-shadow: var(--shadow-dialog);
}

.dialog {
  outline: none;
}

/* In the page: the card that decides inline holds the step (LeadApprove), its title a plain bold
   line rather than the dialog's display heading. */
.inline {
  border: 1px solid var(--border-card);
  border-radius: var(--radius-md);
  background: var(--white);
}

.inline .title {
  font-family: var(--font-body);
  font-size: 17px;
  font-weight: var(--fw-semibold);
  line-height: 1.4;
  letter-spacing: 0;
  text-transform: none;
}

/* --- The step's parts ----------------------------------------------------------------------------- */

.step {
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding: 22px 28px 0;
}

.head {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.eyebrow {
  margin: 0;
  color: var(--text-gold);
}

.title {
  margin: 0;
  color: var(--text-heading);
  font-size: 26px;
  line-height: 1.15;
  letter-spacing: 0.02em;
}

.title:focus {
  outline: none;
}

.title:focus-visible {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.intro {
  margin: 0;
  color: var(--text-secondary);
  font-size: 15px;
  line-height: 1.6;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.fieldLabel {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  align-items: center;
  margin: 0;
  color: var(--text-heading);
  font-size: 15px;
  font-weight: var(--fw-semibold);
}

.required {
  padding: 1px 8px;
  border-radius: var(--radius-pill);
  background: var(--status-waiting-bg);
  color: var(--status-waiting-ink);
  font-size: 12px;
  font-weight: var(--fw-semibold);
}

.optional {
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: var(--fw-regular);
}

.hint {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

/* --- Just me, or for someone (D20, Budget board) ----------------------------------------------------- */

.choice {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

/* Two joined options, the chosen one filled navy and ticked; each wraps rather than overflows. */
.choiceOptions {
  display: flex;
  flex-wrap: wrap;
  align-self: flex-start;
  max-width: 100%;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  background: var(--white);
  overflow: hidden;
}

.choiceOption {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  min-height: 44px;
  padding: 8px 16px;
  color: var(--text-body);
  font-size: 15px;
  cursor: pointer;
}

/* Each option sits in its own field wrapper: the rule goes between the wrappers. */
.choiceOptions > * + * {
  border-left: 1px solid var(--border-control);
}

.choiceOptions > * {
  display: flex;
}

.choiceOption[data-hovered] {
  background: var(--surface-soft);
}

.choiceOption[data-selected] {
  background: var(--brand-navy);
  color: var(--text-inverse);
  font-weight: var(--fw-semibold);
}

.choiceOption[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: -4px;
}

.choiceOption[data-disabled] {
  cursor: default;
  opacity: 0.7;
}

/* --- Who is this for? ------------------------------------------------------------------------------ */

.people {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(200px, 100%), 1fr));
  gap: 10px;
}

.personTile {
  display: flex;
  gap: 10px;
  align-items: center;
  min-height: 60px;
  padding: 10px 12px;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  background: var(--white);
  cursor: pointer;
}

.personTile[data-hovered] {
  border-color: var(--brand-cyan);
}

.personTile[data-selected] {
  border-color: var(--brand-cyan);
  background: var(--brand-cyan-50);
  box-shadow: inset 0 0 0 1px var(--brand-cyan);
}

.personTile[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.personTile[data-disabled] {
  cursor: default;
  opacity: 0.7;
}

.personText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.personName {
  color: var(--text-heading);
  font-size: 15px;
  font-weight: var(--fw-semibold);
}

.personTitle {
  color: var(--text-secondary);
  font-size: 13px;
}

/* The radio's own mark, drawn: a ring, filled when chosen. The word beside it says who. */
.radioMark {
  flex: none;
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-control);
  border-radius: var(--radius-circle);
  background: var(--white);
}

.personTile[data-selected] .radioMark {
  border-color: var(--brand-cyan);
  background: var(--brand-cyan);
  box-shadow: inset 0 0 0 3px var(--white);
}

.fixedPerson {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  align-items: center;
  padding: 12px 14px;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-sm);
  background: var(--surface-soft);
}

.lock {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  margin-left: auto;
  color: var(--text-secondary);
  font-size: 13px;
  white-space: nowrap;
}

.noPeople {
  margin: 0;
  padding: 12px 14px;
  border: 1px dashed var(--border-dashed);
  border-radius: var(--radius-sm);
  color: var(--text-secondary);
  font-size: 14px;
}

/* --- Files ------------------------------------------------------------------------------------------- */

.dropWrap {
  min-width: 0;
}

.drop {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  align-items: center;
  padding: 16px 18px;
  border: 2px dashed var(--border-dashed);
  border-radius: var(--radius-md);
  background: var(--surface-soft);
}

.drop[data-drop-target] {
  border-color: var(--brand-cyan);
  background: var(--brand-cyan-50);
}

.drop[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.dropIcon {
  display: grid;
  flex: none;
  place-items: center;
  color: var(--brand-navy);
}

.dropText {
  display: flex;
  flex: 1 1 260px;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.dropTitle {
  color: var(--text-heading);
  font-size: 15px;
  font-weight: var(--fw-semibold);
}

.dropRefused {
  display: flex;
  gap: 6px;
  align-items: center;
  margin: 8px 0 0;
  color: var(--status-problem-ink);
  font-size: 13px;
  line-height: 1.5;
}

.dropRefused:empty {
  margin: 0;
}

.dropRule {
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

.fileError {
  display: inline-flex;
  gap: 6px;
  align-items: flex-start;
  color: var(--status-problem-ink);
  font-size: 13px;
  line-height: 1.5;
}

/* --- Note ---------------------------------------------------------------------------------------------- */

.textarea {
  width: 100%;
  min-height: 92px;
  padding: 10px 12px;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  background: var(--white);
  color: var(--text-body);
  font: inherit;
  font-size: 15px;
  line-height: 1.5;
  resize: vertical;
}

.textarea[data-focused],
.textarea:focus {
  outline: none;
  border-color: var(--brand-cyan);
  box-shadow: var(--focus-halo);
}

.textarea[data-disabled] {
  background: var(--gray-50);
}

/* --- What happens, and how it's recorded --------------------------------------------------------------- */

.summary {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 20px;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-sm);
  background: var(--surface-soft);
}

.summaryTitle {
  margin: 0;
  color: var(--text-secondary);
}

.summaryList {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.summaryList > li {
  position: relative;
  padding-left: 28px;
  color: var(--text-body);
  font-size: 15px;
  line-height: 1.55;
}

/* A drawn tick before each line (the board's check marks). */
.summaryList > li::before {
  content: '';
  position: absolute;
  top: 4px;
  left: 6px;
  width: 6px;
  height: 11px;
  border: solid var(--status-info-ink);
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.recorded {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-sm);
  background: var(--white);
}

.recordedText {
  margin: 0;
  color: var(--status-info-ink);
  font-size: 15px;
  line-height: 1.5;
}

.recordedText strong {
  color: var(--text-heading);
}

/* A line after the summary, in the info notice's colours (SameAsk board: "If Elena had asked for a
   different split…"). Not a live region: it changes as the person is chosen. */
.tip {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin: 0;
  padding: 12px 16px;
  border: 1px solid var(--info-bg);
  border-radius: var(--radius-sm);
  background: var(--brand-cyan-50);
  color: var(--status-info-ink);
  font-size: 14px;
  line-height: 1.5;
}

.tip > svg {
  flex: none;
  margin-top: 3px;
}

/* --- Errors and the buttons ------------------------------------------------------------------------------ */

.error {
  margin: 0;
  padding: 10px 14px;
  border-left: 3px solid var(--status-problem-ink);
  border-radius: var(--radius-sm);
  background: var(--status-problem-bg);
  color: var(--status-problem-ink);
  font-size: 14px;
  line-height: 1.5;
}

/* The refusal's live region, there and empty until there's one: it takes no room meanwhile (the
   step's gap above it is taken back), and stays in the accessibility tree. */
.errorEmpty {
  margin-top: -22px;
  padding: 0;
  border: 0;
}

.footer {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  align-items: center;
  justify-content: space-between;
  margin: 0 -28px;
  padding: 18px 28px 22px;
  border-top: 1px solid var(--border-hairline);
}

.footnote {
  flex: 1 1 260px;
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

.buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-left: auto;
}

@media (max-width: 560px) {
  .step {
    padding: 18px 16px 0;
  }

  .footer {
    margin: 0 -16px;
    padding: 16px;
  }
}
